import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Paper,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useForm, Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { useEffect, useState } from 'react';
import { useAgencyInfoUpdateApplication } from '../../rest/useAgencyInfoUpdateApplication';
import { showToast } from '../../Toaster/Toaster';

type IProps = {
  handleClose: () => void;
};

interface IAgencyInfoFormInput {
  agency_name: string;
  agency_email: string;
  credential_email: string;
  phone: string;
  cellphone: string;
  member_company_website: string;
  user_member_representative_photo: any;
  user_member_representative_designation: string;
  ownership_status: string;
  civil_aviation_english_copy: any;
  trade_license: any;
  forwarding_letter: any;
  partnership_deed: any;
  incorporation_certificate: any;
  moa: any;
  form_xii: any;
  note?: string;
}

const AddAgencyIfoModal = ({ handleClose }: IProps) => {
  const { user } = useAuthContext();
  const previousAgencyName = user?.user_member_company_name || '';
  const [isChangeEmail, setIsChangeEmail] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<IAgencyInfoFormInput>({
    defaultValues: {
      agency_name: '',
      agency_email: '',
      credential_email: '',
      phone: '',
      cellphone: '',
      member_company_website: '',
      user_member_representative_photo: null,
      user_member_representative_designation: '',
      ownership_status: '',
      civil_aviation_english_copy: null,
      trade_license: null,
      forwarding_letter: null,
      partnership_deed: null,
      incorporation_certificate: null,
      moa: null,
      form_xii: null,
      note: '',
    },
  });

  const { isLoading, mutate, isSuccess } = useAgencyInfoUpdateApplication();

  useEffect(() => {
    if (user?.user_member_company_name) {
      setValue('agency_name', user.user_member_company_name);
    }
  }, [user?.user_member_company_name, setValue]);

  const agencyNameValue = watch('agency_name');
  const ownershipStatusValue = watch('ownership_status');

  const trimmedCurrentName = (agencyNameValue || '').trim();
  const trimmedPrevName = (previousAgencyName || '').trim();
  const isAgencyNameChanged = Boolean(
    trimmedCurrentName && trimmedCurrentName !== trimmedPrevName
  );

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  const validateFile = (
    value: any,
    fieldName: string,
    isRequired: boolean = false
  ) => {
    const hasFile =
      value &&
      (value instanceof FileList
        ? value.length > 0
        : value instanceof File || (Array.isArray(value) && value.length > 0));

    if (isRequired && !hasFile) {
      return `${fieldName} is required`;
    }

    if (hasFile) {
      const file =
        value instanceof FileList
          ? value[0]
          : Array.isArray(value)
            ? value[0]
            : value;
      if (file && file.size && file.size > 5 * 1024 * 1024) {
        return 'File size exceeds 5MB limit.';
      }
    }

    return true;
  };

  const onSubmit = async (data: IAgencyInfoFormInput) => {
    const formData = new FormData();

    if (
      user?.user_member_id !== undefined &&
      user?.user_member_id !== null &&
      String(user?.user_member_id) !== ''
    ) {
      formData.append('agency_member_id', String(user.user_member_id));
    }

    const commonOwnershipFiles = [
      'civil_aviation_english_copy',
      'trade_license',
      'forwarding_letter',
    ];
    const partnershipFiles = ['partnership_deed'];
    const incorporatedFiles = [
      'incorporation_certificate',
      'moa',
      'form_xii',
    ];

    const allOwnershipFiles = [
      ...commonOwnershipFiles,
      ...partnershipFiles,
      ...incorporatedFiles,
    ];

    const fileKeys = [
      'user_member_representative_photo',
      ...allOwnershipFiles,
    ];

    const dataObj = data as Record<string, any>;
    let updatedFieldsCount = 0;

    const trimmedSubmittedName = (data.agency_name || '').trim();
    const trimmedPreviousName = (
      previousAgencyName ||
      user?.user_member_company_name ||
      ''
    ).trim();
    const isNameChanged = Boolean(
      trimmedSubmittedName && trimmedSubmittedName !== trimmedPreviousName
    );

    for (const key in dataObj) {
      const value = dataObj[key];

      if (value === null || value === undefined) {
        continue;
      }

      // If change credential email is not checked, do not send credential_email
      if (!isChangeEmail && key === 'credential_email') {
        continue;
      }

      // If agency name is not changed, do not send agency_name, ownership_status or any ownership files
      if (!isNameChanged) {
        if (
          key === 'agency_name' ||
          key === 'ownership_status' ||
          allOwnershipFiles.includes(key)
        ) {
          continue;
        }
      }

      // If agency name is changed, filter ownership files based on selected status
      if (isNameChanged) {
        if (
          ownershipStatusValue === 'Proprietorship' &&
          (partnershipFiles.includes(key) || incorporatedFiles.includes(key))
        ) {
          continue;
        }
        if (
          ownershipStatusValue === 'Partnership' &&
          incorporatedFiles.includes(key)
        ) {
          continue;
        }
        if (
          ownershipStatusValue === 'Incorporated' &&
          partnershipFiles.includes(key)
        ) {
          continue;
        }
      }

      if (fileKeys.includes(key)) {
        if (value && value.length > 0 && value[0]) {
          formData.append(key, value[0]);
          updatedFieldsCount++;
        } else if (value instanceof File) {
          formData.append(key, value);
          updatedFieldsCount++;
        }
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '' && trimmed !== 'undefined' && trimmed !== 'null') {
          formData.append(key, trimmed);
          updatedFieldsCount++;
        }
      } else {
        formData.append(key, value);
        updatedFieldsCount++;
      }
    }

    if (updatedFieldsCount === 0) {
      showToast('You must change or provide at least one field', 'error');
      return;
    }


    //for console view
    // for (const [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }
    mutate(formData);
  };



  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} p={2}>
      <Grid container spacing={2} mb={2}>
        {/* 0. Previous Agency Name */}
        {previousAgencyName && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                bgcolor: 'rgba(25, 118, 210, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                  }}
                >
                  <BusinessIcon fontSize='small' />
                </Box>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    Previous Agency Name
                  </Typography>
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 700,
                      color: 'primary.dark',
                      lineHeight: 1.3,
                    }}
                  >
                    {previousAgencyName}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label='Current Record'
                size='small'
                color='primary'
                variant='outlined'
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Paper>
          </Grid>
        )}
        {/* 1. Agency Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Agency Name'
            id='agency_name'
            type='text'
            {...register('agency_name', { required: true })}
          />
        </Grid>

        {/* 2. Agency Email */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Agency Email'
            id='agency_email'
            type='email'
            {...register('agency_email')}
          />
        </Grid>
        {/* 8. Change Credential Email Checkbox */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            variant='outlined'
            sx={{
              p: 1,
              px: 1.5,
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 1,
              borderColor: isChangeEmail ? 'primary.main' : 'divider',
              bgcolor: isChangeEmail
                ? 'rgba(25, 118, 210, 0.04)'
                : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            <FormControlLabel
              sx={{ width: '100%', m: 0 }}
              control={
                <Checkbox
                  checked={isChangeEmail}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsChangeEmail(checked);
                    if (!checked) {
                      setValue('credential_email', '');
                      clearErrors('credential_email');
                    }
                  }}
                  color='primary'
                  sx={{ p: 0.5, mr: 1 }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    Want to change credential email?
                  </Typography>
                  {user?.user_member_email && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: 'block',
                        fontSize: '.85rem',
                        lineHeight: 1,
                        textTransform: 'lowercase',
                      }}
                    >
                      Current: {user.user_member_email}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Paper>
        </Grid>

        {/* 9. Credential Email Input (Mandatory when checkbox is ticked) */}
        {isChangeEmail && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label='New Credential Email'
              id='credential_email'
              type='email'
              error={Boolean(errors.credential_email)}
              helperText={
                errors.credential_email?.message
                  ? String(errors.credential_email.message)
                  : ''
              }
              {...register('credential_email', {
                required: isChangeEmail
                  ? 'Credential email is required'
                  : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address',
                },
              })}
            />
          </Grid>
        )}

        {/* 6. Photo */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Owner Photo (max size 5mb)'
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: 'image/jpeg,image/png,image/jpg' }}
            id='user_member_representative_photo'
            type='file'
            {...register('user_member_representative_photo', {
              validate: (val) =>
                validateFile(val, 'Representative photo', false),
            })}
            error={Boolean(errors.user_member_representative_photo)}
            helperText={
              errors.user_member_representative_photo?.message
                ? String(errors.user_member_representative_photo.message)
                : ''
            }
          />
        </Grid>

        {/* 7. Designation */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Designation'
            id='user_member_representative_designation'
            type='text'
            {...register('user_member_representative_designation')}
          />
        </Grid>

        {/* 3. Mobile */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Mobile'
            id='phone'
            type='text'
            {...register('phone')}
          />
        </Grid>

        {/* 4. Telephone */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Telephone'
            id='cellphone'
            type='text'
            {...register('cellphone')}
          />
        </Grid>

        {/* 5. Website */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Website'
            id='member_company_website'
            type='url'
            {...register('member_company_website')}
          />
        </Grid>




        {/* 10. Ownership Status (Conditional when Agency Name differs from previous name) */}
        {isAgencyNameChanged && (
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={Boolean(errors.ownership_status)}
              required
            >
              <InputLabel id='ownership-status-select-label'>
                Ownership Status
              </InputLabel>
              <Controller
                name='ownership_status'
                control={control}
                rules={{
                  required: isAgencyNameChanged
                    ? 'Ownership status is required'
                    : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId='ownership-status-select-label'
                    id='ownership_status'
                    label='Ownership Status'
                  >
                    <MenuItem value='Proprietorship'>Proprietorship</MenuItem>
                    <MenuItem value='Partnership'>Partnership</MenuItem>
                    <MenuItem value='Incorporated'>Incorporated</MenuItem>
                  </Select>
                )}
              />
              {errors.ownership_status && (
                <FormHelperText error>
                  {String(
                    errors.ownership_status.message ||
                    'Ownership status is required'
                  )}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Type-wise Document Uploads */}
        {/* 1, 2, 3 Common Documents */}
        {isAgencyNameChanged && ownershipStatusValue && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='Civil Aviation English Copy (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('civil_aviation_english_copy', {
                  validate: (val) =>
                    validateFile(
                      val,
                      'Civil Aviation English Copy',
                      isAgencyNameChanged
                    ),
                })}
                error={Boolean(errors.civil_aviation_english_copy)}
                helperText={
                  errors.civil_aviation_english_copy?.message
                    ? String(errors.civil_aviation_english_copy.message)
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='Trade License (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('trade_license', {
                  validate: (val) =>
                    validateFile(val, 'Trade License', isAgencyNameChanged),
                })}
                error={Boolean(errors.trade_license)}
                helperText={
                  errors.trade_license?.message
                    ? String(errors.trade_license.message)
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='Forwarding Letter (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('forwarding_letter', {
                  validate: (val) =>
                    validateFile(val, 'Forwarding Letter', isAgencyNameChanged),
                })}
                error={Boolean(errors.forwarding_letter)}
                helperText={
                  errors.forwarding_letter?.message
                    ? String(errors.forwarding_letter.message)
                    : ''
                }
              />
            </Grid>
          </>
        )}

        {/* Partnership: Partnership Deed */}
        {isAgencyNameChanged && ownershipStatusValue === 'Partnership' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type='file'
              label='Partnership Deed (max size 5mb)'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                accept: 'application/pdf,image/jpeg,image/png,image/jpg',
              }}
              {...register('partnership_deed', {
                validate: (val) =>
                  validateFile(
                    val,
                    'Partnership Deed',
                    isAgencyNameChanged &&
                    ownershipStatusValue === 'Partnership'
                  ),
              })}
              error={Boolean(errors.partnership_deed)}
              helperText={
                errors.partnership_deed?.message
                  ? String(errors.partnership_deed.message)
                  : ''
              }
            />
          </Grid>
        )}

        {/* Incorporated: Incorporation Certificate, MoA, Form XII */}
        {isAgencyNameChanged && ownershipStatusValue === 'Incorporated' && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='Incorporation Certificate (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('incorporation_certificate', {
                  validate: (val) =>
                    validateFile(
                      val,
                      'Incorporation Certificate',
                      isAgencyNameChanged &&
                      ownershipStatusValue === 'Incorporated'
                    ),
                })}
                error={Boolean(errors.incorporation_certificate)}
                helperText={
                  errors.incorporation_certificate?.message
                    ? String(errors.incorporation_certificate.message)
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='MoA (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('moa', {
                  validate: (val) =>
                    validateFile(
                      val,
                      'MoA',
                      isAgencyNameChanged &&
                      ownershipStatusValue === 'Incorporated'
                    ),
                })}
                error={Boolean(errors.moa)}
                helperText={
                  errors.moa?.message ? String(errors.moa.message) : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type='file'
                label='Form XII (max size 5mb)'
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: 'application/pdf,image/jpeg,image/png,image/jpg',
                }}
                {...register('form_xii', {
                  validate: (val) =>
                    validateFile(
                      val,
                      'Form XII',
                      isAgencyNameChanged &&
                      ownershipStatusValue === 'Incorporated'
                    ),
                })}
                error={Boolean(errors.form_xii)}
                helperText={
                  errors.form_xii?.message ? String(errors.form_xii.message) : ''
                }
              />
            </Grid>
          </>
        )}

        {/* Note (Optional) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Note'
            id='note'
            placeholder='Enter note (optional)'
            {...register('note')}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box mt={4}>
        {!isLoading ? (
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
          >
            Submit
          </Button>
        ) : (
          <LoadingButton
            loading
            loadingIndicator='Loading...'
            variant='contained'
            fullWidth
            disabled
          >
            <span>Loading...</span>
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
};

export default AddAgencyIfoModal;

